import { useEffect, useRef, useState } from 'react';
import { Button } from '@img/ui';

/**
 * 🗺️ مكوّن اختيار الموقع على الخريطة
 *    - يحمّل Leaflet من CDN (بدون npm install)
 *    - يعرض خريطة OpenStreetMap
 *    - المستخدم يضغط على المكان لتحديد الإحداثيات
 *    - يدعم البحث بـ Nominatim
 *    - يدعم "موقعي الحالي" بـ Geolocation
 */

// 🔧 typings مبسّطة لـ Leaflet (لا نحتاج @types/leaflet)
type LatLng = { lat: number; lng: number };
type LMap = {
  setView: (latlng: [number, number], zoom: number) => LMap;
  remove: () => void;
  on: (evt: string, handler: (e: { latlng: LatLng }) => void) => void;
  getZoom: () => number;
};
type LMarker = {
  setLatLng: (latlng: [number, number]) => LMarker;
  getLatLng: () => LatLng;
  on: (evt: string, handler: () => void) => void;
  addTo: (map: LMap) => LMarker;
};
interface LeafletGlobal {
  map: (el: HTMLElement) => LMap;
  marker: (latlng: [number, number], opts?: { draggable?: boolean }) => LMarker;
  tileLayer: (url: string, opts: { attribution: string; maxZoom: number; opacity?: number }) => { addTo: (m: LMap) => unknown };
  Icon: { Default: { prototype: object; mergeOptions: (o: object) => void } };
}

declare global {
  interface Window {
    L?: LeafletGlobal;
    __leafletLoading__?: Promise<LeafletGlobal>;
  }
}

const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';

function loadLeaflet(): Promise<LeafletGlobal> {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.L) return Promise.resolve(window.L);
  if (window.__leafletLoading__) return window.__leafletLoading__;

  window.__leafletLoading__ = new Promise<LeafletGlobal>((resolve, reject) => {
    // CSS
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    // JS
    if (document.querySelector(`script[src="${LEAFLET_JS}"]`)) {
      // ينتظر التحميل
      const t = setInterval(() => {
        if (window.L) { clearInterval(t); resolve(window.L); }
      }, 50);
      setTimeout(() => { clearInterval(t); if (!window.L) reject(new Error('Leaflet timeout')); }, 10000);
      return;
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => {
      if (window.L) resolve(window.L);
      else reject(new Error('Leaflet failed to load'));
    };
    script.onerror = () => reject(new Error('Failed to fetch Leaflet from CDN'));
    document.head.appendChild(script);
  });
  return window.__leafletLoading__;
}

export function LocationPicker({
  value,
  onChange,
  city,
  height = 300,
  label,
}: {
  value?: { lat: number | null; lng: number | null; address?: string | null };
  onChange: (v: {
    lat: number;
    lng: number;
    address?: string;
    detectedCity?: string;
    detectedDistrict?: string;
  }) => void;
  city?: string;
  height?: number;
  label?: string;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMap = useRef<LMap | null>(null);
  const marker = useRef<LMarker | null>(null);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 🚀 تحميل Leaflet من CDN
  useEffect(() => {
    let mounted = true;
    loadLeaflet()
      .then((L) => {
        if (!mounted || !mapRef.current) return;

        // إصلاح أيقونة الـ marker الافتراضية
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (L.Icon.Default.prototype as any)._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        });

        const start = value?.lat && value?.lng
          ? { lat: Number(value.lat), lng: Number(value.lng) }
          : getCityCoords(city) ?? { lat: 24.7136, lng: 46.6753 };

        const m = L.map(mapRef.current).setView([start.lat, start.lng], 16);

        // 🛰️ تصوير جوي (Esri World Imagery) — مجاني بدون مفتاح
        L.tileLayer(
          'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          { attribution: 'Tiles © Esri', maxZoom: 19 },
        ).addTo(m);
        // ✏️ طبقة العناوين فوق التصوير الجوي (شفّافة، تظهر أسماء الشوارع)
        L.tileLayer(
          'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
          { attribution: '© Esri', maxZoom: 19, opacity: 0.85 },
        ).addTo(m);

        marker.current = L.marker([start.lat, start.lng], { draggable: true }).addTo(m);

        m.on('click', (e) => {
          const { lat, lng } = e.latlng;
          marker.current?.setLatLng([lat, lng]);
          reverseGeocode(lat, lng).then((r) => onChange({
            lat, lng,
            address: r.address ?? undefined,
            detectedCity: r.detectedCity ?? undefined,
            detectedDistrict: r.detectedDistrict ?? undefined,
          }));
        });

        marker.current.on('dragend', () => {
          const { lat, lng } = marker.current!.getLatLng();
          reverseGeocode(lat, lng).then((r) => onChange({
            lat, lng,
            address: r.address ?? undefined,
            detectedCity: r.detectedCity ?? undefined,
            detectedDistrict: r.detectedDistrict ?? undefined,
          }));
        });

        leafletMap.current = m;
        setLoaded(true);
      })
      .catch((e: Error) => {
        console.warn('LocationPicker: تعذّر تحميل الخريطة', e);
        setError(e.message ?? 'تعذّر تحميل الخريطة');
      });

    return () => {
      mounted = false;
      try { leafletMap.current?.remove(); } catch { /* ignore */ }
      leafletMap.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🌍 لما يتغير الموقع برّاً، انقل الـ marker
  useEffect(() => {
    if (!leafletMap.current || !marker.current) return;
    if (value?.lat && value?.lng) {
      const lat = Number(value.lat); const lng = Number(value.lng);
      marker.current.setLatLng([lat, lng]);
      leafletMap.current.setView([lat, lng], leafletMap.current.getZoom());
    }
  }, [value?.lat, value?.lng]);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      alert('متصفحك لا يدعم تحديد الموقع');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        marker.current?.setLatLng([latitude, longitude]);
        leafletMap.current?.setView([latitude, longitude], 16);
        reverseGeocode(latitude, longitude).then((r) =>
          onChange({
            lat: latitude, lng: longitude,
            address: r.address ?? undefined,
            detectedCity: r.detectedCity ?? undefined,
            detectedDistrict: r.detectedDistrict ?? undefined,
          }),
        );
      },
      () => alert('تعذّر الحصول على موقعك'),
      { enableHighAccuracy: true },
    );
  };

  // 🎯 ركّز على النقطة المحددة حالياً (الدبوس)
  const focusMyPin = () => {
    if (!marker.current || !leafletMap.current) return;
    if (!value?.lat || !value?.lng) {
      alert('لم تحدّد نقطة بعد — اضغط على الخريطة أولاً');
      return;
    }
    const lat = Number(value.lat);
    const lng = Number(value.lng);
    leafletMap.current.setView([lat, lng], 17);
    marker.current.setLatLng([lat, lng]);
  };

  const doSearch = async () => {
    if (!search.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(search + ', ' + (city ?? 'السعودية'))}&limit=1&accept-language=ar`,
      );
      const json = await res.json();
      if (json.length > 0) {
        const lat = parseFloat(json[0].lat);
        const lng = parseFloat(json[0].lon);
        marker.current?.setLatLng([lat, lng]);
        leafletMap.current?.setView([lat, lng], 16);
        // ✨ بعد البحث، نستدعي reverseGeocode لاستخراج المدينة/الحي بشكل أدق
        const r = await reverseGeocode(lat, lng);
        onChange({
          lat, lng,
          address: r.address ?? json[0].display_name,
          detectedCity: r.detectedCity ?? undefined,
          detectedDistrict: r.detectedDistrict ?? undefined,
        });
      } else {
        alert('لم يُعثر على هذا الموقع');
      }
    } catch {
      alert('خطأ في البحث');
    } finally { setSearching(false); }
  };

  // 🆘 fallback لو Leaflet ما تحمّل (CDN مقطوع، انترنت ضعيف…)
  if (error) {
    return (
      <div>
        {label && <label className="block text-sm font-bold text-ink mb-1">{label}</label>}
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 text-sm">
          <div className="font-bold text-amber-800 mb-1">⚠️ تعذّر تحميل الخريطة</div>
          <p className="text-amber-700 text-xs mb-3">{error} — يمكنك إدخال الإحداثيات يدوياً:</p>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number" step="0.0000001" placeholder="خط العرض (lat)"
              value={value?.lat ?? ''}
              onChange={(e) => {
                const lat = Number(e.target.value);
                if (!isNaN(lat) && value?.lng) onChange({ lat, lng: Number(value.lng) });
              }}
              dir="ltr" className="px-3 py-2 rounded-lg border-2 border-amber-200 bg-white text-sm"
            />
            <input
              type="number" step="0.0000001" placeholder="خط الطول (lng)"
              value={value?.lng ?? ''}
              onChange={(e) => {
                const lng = Number(e.target.value);
                if (!isNaN(lng) && value?.lat) onChange({ lat: Number(value.lat), lng });
              }}
              dir="ltr" className="px-3 py-2 rounded-lg border-2 border-amber-200 bg-white text-sm"
            />
          </div>
          <Button size="sm" variant="ghost" onClick={useMyLocation} className="mt-2">📍 استخدم موقعي الحالي</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {label && <label className="block text-sm font-bold text-ink mb-1">{label}</label>}

      {/* أزرار الإجراءات */}
      <div className="flex gap-2 flex-wrap mb-2">
        <input
          type="text"
          placeholder="🔍 ابحث عن عنوان..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), doSearch())}
          className="flex-1 min-w-[160px] px-4 py-2 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
        />
        <Button size="sm" variant="ghost" onClick={doSearch} loading={searching}>بحث</Button>
        <Button size="sm" variant="ghost" onClick={useMyLocation} title="موقعي الحالي">📍 موقعي</Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={focusMyPin}
          title="ركّز على النقطة المحددة"
          disabled={!value?.lat || !value?.lng}
        >
          🎯 نقطتي
        </Button>
      </div>

      {/* الخريطة + زر عائم للتركيز على النقطة */}
      <div className="relative">
        <div
          ref={mapRef}
          style={{ height: `${height}px` }}
          className="w-full rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-50"
        >
          {!loaded && (
            <div className="w-full h-full flex items-center justify-center text-ink-muted text-sm">
              ⟳ جاري تحميل الخريطة…
            </div>
          )}
        </div>

        {/* 🎯 زر عائم سريع للتركيز على النقطة المحددة (يظهر فقط لو في نقطة) */}
        {loaded && value?.lat && value?.lng && (
          <button
            onClick={focusMyPin}
            title="ارجع لنقطتي"
            className="absolute bottom-3 left-3 z-[400] w-11 h-11 rounded-full bg-white shadow-lg border-2 border-navy hover:bg-navy hover:text-white transition flex items-center justify-center text-lg"
          >
            🎯
          </button>
        )}

        {/* 📍 زر عائم سريع للموقع الحالي */}
        {loaded && (
          <button
            onClick={useMyLocation}
            title="موقعي الحالي"
            className="absolute bottom-3 left-16 z-[400] w-11 h-11 rounded-full bg-white shadow-lg border-2 border-gray-300 hover:border-navy hover:bg-navy-soft transition flex items-center justify-center text-lg"
          >
            📍
          </button>
        )}
      </div>

      {/* عرض الإحداثيات */}
      {value?.lat && value?.lng && (
        <div className="mt-2 text-xs text-ink-muted flex flex-wrap gap-2">
          📍 <span dir="ltr" className="font-mono">{Number(value.lat).toFixed(6)}, {Number(value.lng).toFixed(6)}</span>
          {value.address && <span className="text-ink truncate">— {value.address}</span>}
        </div>
      )}

      <p className="text-xs text-ink-muted mt-2">
        💡 اضغط على الخريطة لتحديد الموقع، أو اسحب العلامة 📍 للتعديل، أو ابحث بالاسم.
      </p>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Helpers ━━━━━━━━━━━━━━━━━━━━

const CITY_COORDS: Record<string, { lat: number; lng: number }> = {
  'الرياض': { lat: 24.7136, lng: 46.6753 },
  'جدة': { lat: 21.4858, lng: 39.1925 },
  'مكة المكرمة': { lat: 21.3891, lng: 39.8579 },
  'المدينة المنورة': { lat: 24.5247, lng: 39.5692 },
  'الدمام': { lat: 26.4207, lng: 50.0888 },
  'الخبر': { lat: 26.2172, lng: 50.1971 },
  'الظهران': { lat: 26.2361, lng: 50.0393 },
  'الجبيل': { lat: 27.0046, lng: 49.6602 },
  'الأحساء': { lat: 25.4295, lng: 49.5891 },
  'الطائف': { lat: 21.2703, lng: 40.4158 },
  'تبوك': { lat: 28.3838, lng: 36.5550 },
  'بريدة': { lat: 26.3260, lng: 43.9750 },
  'حائل': { lat: 27.5219, lng: 41.6907 },
  'أبها': { lat: 18.2164, lng: 42.5053 },
  'خميس مشيط': { lat: 18.3060, lng: 42.7264 },
  'نجران': { lat: 17.4924, lng: 44.1277 },
  'جازان': { lat: 16.8892, lng: 42.5511 },
  'الباحة': { lat: 20.0129, lng: 41.4677 },
  'سكاكا': { lat: 29.9698, lng: 40.2064 },
  'عرعر': { lat: 30.9753, lng: 41.0381 },
  'ينبع': { lat: 24.0883, lng: 38.0617 },
};

function getCityCoords(city?: string) {
  if (!city) return null;
  return CITY_COORDS[city] ?? null;
}

// 🏙️ قائمة مدن السعودية المعتمدة في النظام (لمطابقة Nominatim)
const KNOWN_SAUDI_CITIES = Object.keys(CITY_COORDS);

interface ReverseGeocodeResult {
  address: string | null;
  detectedCity: string | null;     // مدينة مطابقة لقائمتنا (إن وُجدت)
  detectedDistrict: string | null; // الحي/المنطقة الفرعية
}

async function reverseGeocode(lat: number, lng: number): Promise<ReverseGeocodeResult> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=ar&addressdetails=1&zoom=18`,
    );
    const json = await res.json();
    const addr = json.address ?? {};

    // 🏙️ استخراج المدينة من حقول متعددة (Nominatim يستخدم تسميات مختلفة):
    //   - city / town / village (الحالة الأساسية)
    //   - municipality / county (محافظة)
    //   - state / region (منطقة، مثل "منطقة الرياض")
    // نجرّب كلها وقت المطابقة لأن المنطقة الجغرافية قد تكون داخل محافظة فرعية (مثل الدرعية في الرياض).
    const candidates: string[] = [
      addr.city, addr.town, addr.village, addr.municipality,
      addr.county, addr.state, addr.region,
    ].filter(Boolean) as string[];

    // 🧹 تنظيف الاسم: شيل "منطقة"، "محافظة"، "بلدية"، "مدينة"
    const cleanName = (s: string): string =>
      s.replace(/^(منطقة|محافظة|بلدية|مدينة|أمانة|إمارة)\s+/, '').trim();

    let detectedCity: string | null = null;
    for (const raw of candidates) {
      const cleaned = cleanName(raw);
      // مطابقة دقيقة أولاً
      let match = KNOWN_SAUDI_CITIES.find((k) => k === cleaned);
      // ثم مطابقة جزئية (مع cleanup)
      if (!match) match = KNOWN_SAUDI_CITIES.find((k) => cleaned.includes(k) || k.includes(cleaned));
      // ثم مطابقة جزئية مع الاسم الأصلي (بدون cleanup) للحالات النادرة
      if (!match) match = KNOWN_SAUDI_CITIES.find((k) => raw.includes(k));
      if (match) { detectedCity = match; break; }
    }
    // لو ما لقينا تطابق، خذ أول candidate نظيف
    if (!detectedCity && candidates.length > 0) {
      detectedCity = cleanName(candidates[0]);
    }

    // 🏘️ استخراج الحي — Nominatim يستخدم suburb/neighbourhood/quarter
    let detectedDistrict: string | null =
      addr.suburb ?? addr.neighbourhood ?? addr.quarter ?? addr.city_district ?? addr.residential ?? null;
    // 🧹 شيل البادئة "حي" (مثلاً "حي حطين" → "حطين")
    if (detectedDistrict) {
      detectedDistrict = detectedDistrict.replace(/^حي\s+/, '').trim();
    }

    return {
      address: json.display_name ?? null,
      detectedCity,
      detectedDistrict,
    };
  } catch {
    return { address: null, detectedCity: null, detectedDistrict: null };
  }
}
